import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";

const TYPE_KEYS = ["mcq", "true_false", "open", "scenario", "case_study", "exam"];
const DIFF_KEYS = ["beginner", "intermediate", "advanced"];
const LENGTHS = [5, 10, 20];

export const QuizConfig = ({ mode = "quiz", onStart, busy }) => {
    const { t } = useTranslation();
    const [types, setTypes] = useState(mode === "exam" ? ["mcq", "true_false", "exam"] : ["mcq", "true_false", "open"]);
    const [diff, setDiff] = useState("intermediate");
    const [count, setCount] = useState(mode === "exam" ? 10 : 5);
    const [minutes, setMinutes] = useState(10);

    const toggle = (t2) => setTypes((c) => c.includes(t2) ? c.filter((x) => x !== t2) : [...c, t2]);

    return (
        <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="quiz-config">
            <div className="space-y-5">
                <div>
                    <div className="mono-label mb-2 text-t4">{t("quiz.questionTypes")}</div>
                    <div className="flex flex-wrap gap-1.5">
                        {TYPE_KEYS.map((k) => (
                            <button key={k} data-testid={`quiz-type-${k}`} onClick={() => toggle(k)}
                                className={cn("rounded-lg px-3 py-1.5 text-xs transition-colors", types.includes(k) ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-surface-2 text-t3 hover:text-t1")}>{t(`quiz.types.${k}`)}</button>
                        ))}
                    </div>
                </div>
                <div className="flex flex-wrap gap-6">
                    <div>
                        <div className="mono-label mb-2 text-t4">{t("quiz.difficulty")}</div>
                        <div className="flex gap-1.5">
                            {DIFF_KEYS.map((d) => (
                                <button key={d} data-testid={`quiz-diff-${d}`} onClick={() => setDiff(d)}
                                    className={cn("rounded-lg px-3 py-1.5 text-xs transition-colors", diff === d ? "bg-surface-3 text-t1" : "bg-surface-2 text-t3")}>{t(`quiz.diffs.${d}`)}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="mono-label mb-2 text-t4">{t("quiz.questions")}</div>
                        <div className="flex gap-1.5">
                            {LENGTHS.map((l) => (
                                <button key={l} data-testid={`quiz-len-${l}`} onClick={() => setCount(l)}
                                    className={cn("rounded-lg px-3 py-1.5 text-xs transition-colors", count === l ? "bg-surface-3 text-t1" : "bg-surface-2 text-t3")}>{l}</button>
                            ))}
                        </div>
                    </div>
                    {mode === "exam" && (
                        <div>
                            <div className="mono-label mb-2 text-t4">{t("quiz.timeLimit")}</div>
                            <div className="flex gap-1.5">
                                {[5, 10, 20, 30].map((m) => (
                                    <button key={m} data-testid={`quiz-time-${m}`} onClick={() => setMinutes(m)}
                                        className={cn("rounded-lg px-3 py-1.5 text-xs transition-colors", minutes === m ? "bg-surface-3 text-t1" : "bg-surface-2 text-t3")}>{m}m</button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <Button data-testid="quiz-start-button" className="gap-2" disabled={busy || !types.length}
                    onClick={() => onStart({ count, types, difficulty: diff, mode, time_limit_minutes: mode === "exam" ? minutes : null })}>
                    <Play className="h-4 w-4" /> {mode === "exam" ? t("quiz.startExam") : t("quiz.startQuiz")}
                </Button>
            </div>
        </div>
    );
};
